import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import ModalSurveySatisfaction, {
  SurveySatisfactionAnswer,
} from '../Modals/ModalSurveySatisfaction';
import { LoggedUser } from '../../db/db';
import { CsatSurveySession } from '../../models/Csat';
import { addCsatResponse, dismissCsatSurveyView, startCsatSurvey } from '../../services/csat';
import {
  buildCsatTrackingData,
  consumePendingCsatSurveyTrigger,
  PENDING_CSAT_TRIGGER_EVENT,
} from '../../utils/csat';

type Props = {
  user?: LoggedUser[];
};

type ActiveCsatSurveyState = CsatSurveySession & {
  contextType: string | null;
  contextId: string | null;
};

const CsatSurveyManager = ({ user }: Props) => {
  const location = useLocation();
  const [activeCsatSurvey, setActiveCsatSurvey] = useState<ActiveCsatSurveyState | null>(null);
  const isProcessingRef = useRef(false);
  const skipDismissOnCloseRef = useRef(false);

  const processPendingTrigger = useCallback(async () => {
    if (isProcessingRef.current || activeCsatSurvey || !user?.[0]?.accessToken) {
      return;
    }

    const pendingTrigger = consumePendingCsatSurveyTrigger();

    if (!pendingTrigger) {
      return;
    }

    isProcessingRef.current = true;

    try {
      const response = await startCsatSurvey(
        {
          triggerContext: pendingTrigger.triggerContext,
          contextType: pendingTrigger.contextType ?? null,
          contextId: pendingTrigger.contextId ?? null,
          ...buildCsatTrackingData(),
        },
        user[0].accessToken
      );

      if (response.status === 204 || !response.data?.csatSurvey || !response.data?.csatSurveyView) {
        return;
      }

      setActiveCsatSurvey({
        ...response.data,
        contextType: pendingTrigger.contextType ?? null,
        contextId: pendingTrigger.contextId ?? null,
      });
    } catch (error) {
      console.error(error);
    } finally {
      isProcessingRef.current = false;
    }
  }, [activeCsatSurvey, user]);

  useEffect(() => {
    void processPendingTrigger();
  }, [location.key, location.pathname, location.search, processPendingTrigger]);

  useEffect(() => {
    const handlePendingTrigger = () => {
      void processPendingTrigger();
    };

    window.addEventListener(PENDING_CSAT_TRIGGER_EVENT, handlePendingTrigger);

    return () => {
      window.removeEventListener(PENDING_CSAT_TRIGGER_EVENT, handlePendingTrigger);
    };
  }, [processPendingTrigger]);

  const handleCloseSurvey = useCallback(async () => {
    if (skipDismissOnCloseRef.current) {
      skipDismissOnCloseRef.current = false;
      setActiveCsatSurvey(null);
      return;
    }

    if (!activeCsatSurvey || !user?.[0]?.accessToken) {
      setActiveCsatSurvey(null);
      return;
    }

    try {
      await dismissCsatSurveyView(
        {
          surveyId: activeCsatSurvey.csatSurvey.id,
          surveyViewId: activeCsatSurvey.csatSurveyView.id,
        },
        user[0].accessToken
      );
    } catch (error) {
      console.error(error);
    } finally {
      setActiveCsatSurvey(null);
    }
  }, [activeCsatSurvey, user]);

  const handleSubmitSurvey = useCallback(
    async (answer: SurveySatisfactionAnswer) => {
      if (!activeCsatSurvey || !user?.[0]?.accessToken) {
        return;
      }

      await addCsatResponse(
        {
          surveyId: activeCsatSurvey.csatSurvey.id,
          surveyViewId: activeCsatSurvey.csatSurveyView.id,
          score: answer.score,
          comment: answer.comment || null,
          contextType: activeCsatSurvey.contextType,
          contextId: activeCsatSurvey.contextId,
          ...buildCsatTrackingData(),
        },
        user[0].accessToken
      );

      skipDismissOnCloseRef.current = true;
    },
    [activeCsatSurvey, user]
  );

  if (!activeCsatSurvey) {
    return null;
  }

  return (
    <div className='modal'>
      <ModalSurveySatisfaction
        surveySubject={activeCsatSurvey.csatSurvey.questionTitle}
        surveyDescription={activeCsatSurvey.csatSurvey.description}
        onSubmit={handleSubmitSurvey}
        onCancel={handleCloseSurvey}
      />
    </div>
  );
};

export default CsatSurveyManager;
